import React from "react";
import { X, ExternalLink } from "lucide-react";
import { findScholarship } from "@/data/scholarships";
import { benefitCoverage, benefitCategories } from "@/data/roadmap";
import { computeMatch, daysUntil } from "@/lib/calc";
import { EligibilityBadge, StatusBadge } from "@/components/common";

export function CompareModal({ ids, profile, onClose }) {
  const items = ids.map((id) => findScholarship(id)).filter(Boolean);
  const matches = items.map((s) => computeMatch(profile, s));

  const rows = [
    { label: "Match", render: (s, i) => <span className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] px-2.5 py-0.5 text-xs font-bold text-white">{matches[i].matchScore}%</span> },
    { label: "Negara", render: (s) => s.country },
    { label: "Penyelenggara", render: (s) => s.provider },
    { label: "Jenjang", render: (s) => s.degree },
    { label: "Pendanaan", render: (s) => <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">{s.funding}</span> },
    { label: "Deadline", render: (s) => { const d = daysUntil(s.deadline); return s.deadline ? <span>{s.deadline}{d > 0 ? <span className="ml-1 text-xs text-slate-400">({d} hari)</span> : <span className="ml-1 text-xs text-red-500">(lewat)</span>}</span> : <span className="text-slate-400">Belum diverifikasi</span>; } },
    { label: "Min GPA", render: (s) => s.minGpa },
    { label: `Min ${items[0]?.language?.test || "IELTS"}`, render: (s) => s.language.min },
    { label: "Pengalaman", render: (s) => (s.experienceYears ? `${s.experienceYears} thn` : "Tidak wajib") },
    { label: "Status", render: (s) => <StatusBadge status={s.status} /> },
    { label: "Ringkasan Kelayakan", render: (s, i) => (
      <span className="flex flex-wrap gap-1">
        <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">{matches[i].passes} PASS</span>
        <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-600">{matches[i].unknowns} UNKNOWN</span>
        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-600">{matches[i].fails} FAIL</span>
      </span>
    ) },
    { label: "Benefit Utama", render: (s) => {
      const cov = benefitCoverage[s.id] || {};
      const entries = Object.entries(cov).slice(0, 6);
      if (!entries.length) return <span className="text-slate-400">Belum diverifikasi</span>;
      return (
        <ul className="space-y-0.5">
          {entries.map(([bid, c]) => {
            const cat = benefitCategories.find((x) => x.id === bid);
            return <li key={bid} className="text-xs"><span className="text-slate-600">{cat?.label || bid}:</span> <span className="font-medium text-[#6D28D9]">{c.status}</span></li>;
          })}
        </ul>
      );
    } },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-h-[88vh] w-full max-w-4xl overflow-auto rounded-3xl bg-white p-6 shadow-2xl" data-testid="compare-modal">
        <button onClick={onClose} className="absolute right-4 top-4 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="compare-close"><X className="h-5 w-5" /></button>
        <h2 className="font-display text-xl font-extrabold text-slate-900">Perbandingan Beasiswa</h2>
        <p className="mt-1 text-sm text-slate-500">Bandingkan match & benefit berdampingan. Skor match adalah diagnostik, bukan probabilitas penerimaan.</p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-white p-3 text-left text-xs font-semibold text-slate-400">Kriteria</th>
                {items.map((s) => (
                  <th key={s.id} className="min-w-[180px] p-3 text-left align-top">
                    <div className="font-display text-sm font-bold text-slate-900">{s.name}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={row.label} className={ri % 2 ? "bg-[#FBFAFF]" : ""}>
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-inherit p-3 align-top text-xs font-medium text-slate-500">{row.label}</td>
                  {items.map((s, i) => <td key={s.id} className="p-3 align-top text-slate-700">{row.render(s, i)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
